import React from "react";
import { ActionIcon, Badge, Box, Text, Tooltip } from "@mantine/core";
import { IconTrash } from "@tabler/icons-react";
import { CashTransaction } from "../../types/cashDrawer";
import {
  formatCurrency,
  formatDrawerDateTime,
  formatTransactionType,
} from "../../utils/cashDrawerUtils";
import { StandardTable, StandardTableColumn } from "../common/StandardTable";

interface Props {
  transactions: CashTransaction[];
  openingBalance: number;
  canVoid: boolean;
  onVoid?: (tx: CashTransaction) => void;
  timezone: string;
}

interface CashRow {
  tx: CashTransaction;
  signed: number;
  running: number;
}

export const CashTransactionsTable: React.FC<Props> = ({
  transactions,
  openingBalance,
  canVoid,
  onVoid,
  timezone,
}) => {
  let running = Number(openingBalance) || 0;
  const rows: CashRow[] = transactions.map((tx) => {
    const signed = Number(tx.direction) * Number(tx.amount);
    if (!tx.voided_at && tx.type !== "opening_balance") {
      running += signed;
    }
    return { tx, signed, running };
  });

  const columns: StandardTableColumn<CashRow>[] = [
    {
      key: "time",
      label: "Time",
      render: ({ tx }) => (
        <Text size="sm" style={{ opacity: tx.voided_at ? 0.5 : 1 }}>
          {formatDrawerDateTime(tx.created_dt, timezone)}
        </Text>
      ),
    },
    {
      key: "type",
      label: "Type",
      render: ({ tx }) => (
        <Box component="span" style={{ opacity: tx.voided_at ? 0.5 : 1 }}>
          <Badge
            color={
              tx.direction > 0
                ? "var(--brand-lime-green)"
                : "var(--brand-burnt-orange)"
            }
          >
            {formatTransactionType(tx)}
          </Badge>
          {tx.voided_at && (
            <Badge ml="xs" color="gray" variant="outline">
              Voided
            </Badge>
          )}
        </Box>
      ),
    },
    {
      key: "description",
      label: "Description",
      render: ({ tx }) => (
        <Text size="sm" style={{ opacity: tx.voided_at ? 0.5 : 1 }}>
          {tx.description || "—"}
        </Text>
      ),
    },
    {
      key: "amount",
      label: "Amount",
      align: "right",
      render: ({ tx, signed }) => (
        <Text
          c={signed >= 0 ? "green.7" : "red.7"}
          fw={600}
          style={{ opacity: tx.voided_at ? 0.5 : 1 }}
        >
          {formatCurrency(signed, { showSign: true })}
        </Text>
      ),
    },
    {
      key: "running",
      label: "Running",
      align: "right",
      render: ({ tx, running: runningBal }) => (
        <Box component="span" style={{ opacity: tx.voided_at ? 0.5 : 1 }}>
          {formatCurrency(runningBal)}
        </Box>
      ),
    },
    {
      key: "actions",
      label: "",
      align: "right",
      width: 56,
      render: ({ tx }) =>
        canVoid &&
        !tx.voided_at &&
        !tx.linked_payment_id &&
        tx.type !== "opening_balance" &&
        tx.type !== "closeout" ? (
          <Tooltip label="Void transaction">
            <ActionIcon
              variant="subtle"
              color="red"
              onClick={() => onVoid?.(tx)}
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Tooltip>
        ) : null,
    },
  ];

  return (
    <StandardTable<CashRow>
      columns={columns}
      data={rows}
      getRowKey={({ tx }) => tx.id}
      ariaLabel="Cash transactions"
      stickyHeader
      emptyState={
        <Text c="dimmed" size="sm">
          No transactions recorded for this drawer yet.
        </Text>
      }
    />
  );
};

export default CashTransactionsTable;
